import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { UserCheck, Save, CheckCircle } from 'lucide-react';

export default function RecordShowPage() {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    location_id: '',
    patient_first_name: '',
    patient_last_name: '',
    patient_email: '',
    patient_phone: '',
    is_qualified: false,
    show_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    axios.get(`${API}/locations`)
      .then(r => setLocations(r.data.filter(l => l.active)))
      .catch(() => {});
  }, []);

  const setField = (name, val) => setForm(prev => ({ ...prev, [name]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.location_id || !form.patient_first_name || !form.patient_last_name) {
      toast.error('Location, first name, and last name are required');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/shows`, form);
      toast.success(`Show recorded for ${form.patient_first_name} ${form.patient_last_name}`);
      
      // Reset form
      setForm({
        location_id: form.location_id, // Keep location selected
        patient_first_name: '',
        patient_last_name: '',
        patient_email: '',
        patient_phone: '',
        is_qualified: false,
        show_date: new Date().toISOString().split('T')[0]
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to record show');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="font-heading text-xl font-bold text-white tracking-tight flex items-center gap-2">
          <UserCheck size={22} className="text-blue-400" />
          Record a Show
        </h1>
        <p className="text-sm text-slate-400 mt-1">Track consult shows and qualification status</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Location */}
        <Card className="bg-white shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-heading">Location</CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={form.location_id} onValueChange={v => setField('location_id', v)}>
              <SelectTrigger className="bg-white">
                <SelectValue placeholder="Select location *" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {locations.map(l => (
                  <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Patient Info */}
        <Card className="bg-white shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-heading">Patient Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-slate-700 text-sm font-medium">First Name <span className="text-red-500">*</span></Label>
                <Input
                  value={form.patient_first_name}
                  onChange={e => setField('patient_first_name', e.target.value)}
                  placeholder="John"
                  className="bg-white border-slate-200"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-700 text-sm font-medium">Last Name <span className="text-red-500">*</span></Label>
                <Input
                  value={form.patient_last_name}
                  onChange={e => setField('patient_last_name', e.target.value)}
                  placeholder="Smith"
                  className="bg-white border-slate-200"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-700 text-sm font-medium">Email <span className="text-slate-400 text-xs">(optional)</span></Label>
                <Input
                  value={form.patient_email}
                  onChange={e => setField('patient_email', e.target.value)}
                  type="email"
                  placeholder="patient@email.com"
                  className="bg-white border-slate-200"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-700 text-sm font-medium">Phone <span className="text-slate-400 text-xs">(optional)</span></Label>
                <Input
                  value={form.patient_phone}
                  onChange={e => setField('patient_phone', e.target.value)}
                  type="tel"
                  placeholder="(555) 000-0000"
                  className="bg-white border-slate-200"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Qualification */}
        <Card className="bg-white shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-heading">Qualification Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 rounded-lg" 
              style={{ background: form.is_qualified ? 'rgba(34,197,94,0.1)' : 'rgba(148,163,184,0.1)' }}>
              <div>
                <p className="font-medium text-slate-800">
                  {form.is_qualified ? 'Qualified for Full Arch Treatment' : 'Not Qualified'}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {form.is_qualified 
                    ? 'Patient meets criteria for Full Arch consultation'
                    : 'Patient does not meet Full Arch criteria'}
                </p>
              </div>
              <Switch
                checked={form.is_qualified}
                onCheckedChange={v => setField('is_qualified', v)}
                className="data-[state=checked]:bg-green-600"
              />
            </div>
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex gap-3">
          <Button
            type="submit"
            disabled={loading}
            className="gap-2 font-semibold flex-1"
            style={{ background: '#2563EB', color: 'white' }}
          >
            {loading ? 'Saving...' : <><CheckCircle size={15} /> Record Show</>}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/eod/records')}
            className="bg-white"
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
